import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ProtectedRoute } from '@/app';
import { AuthGuard } from './AuthGuard';
import { AppLayout } from '@/shared/ui/Layout/AppLayout';
import { useLayout } from '@/features/profile';
import { useAuth } from '@/features/auth';
import { LayoutConfig } from '@/entities/user';

const LoginPage = React.lazy(() => import('@/pages/Login'));
const RegisterPage = React.lazy(() => import('@/pages/Register'));
const DownloadSDKPage = React.lazy(() => import('@/widgets/dooh/DownloadSDK'));
const ForgotPasswordPage = React.lazy(() => import('@/pages/ForgotPassword'));
const ResetPasswordPage = React.lazy(() => import('@/pages/ResetPassword'));
const ProfilePage = React.lazy(() => import('@/pages/Profile'));
const DynamicPage = React.lazy(() => import('@/pages/DynamicPage'));
const NotFoundPage = React.lazy(() => import('@/pages/NotFound'));
const ErrorPage = React.lazy(() => import('@/pages/Error'));

const PageLoader: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body1" color="text.secondary">
      Loading page...
    </Typography>
  </Box>
);

const AppRoutes: React.FC<{
  layout?: LayoutConfig;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
}> = ({ layout, isLoading, isError, error }) => {
  if (isError) {
    return (
      <ErrorPage error={error ?? new Error('Failed to load the user layout')} />
    );
  }

  if (isLoading || !layout) {
    return <PageLoader />;
  }

  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/download_sdk" element={<DownloadSDKPage />} />

          <Route path="/:pageid" element={<DynamicPage layout={layout} />} />

          <Route
            path="/"
            element={
              <Navigate to={layout?.routes?.[0] ?? '/app/home'} replace />
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
};

const AppRouter: React.FC = () => {
  const {
    user: authenticatedUser,
    isAuthenticated,
    isLoading: authLoading,
  } = useAuth();

  const {
    layout,
    isLoading: layoutLoading,
    isError: layoutError,
    error,
  } = useLayout(isAuthenticated, authenticatedUser?.uid);
  if (authLoading) {
    return <PageLoader />;
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/login"
            element={
              <AuthGuard>
                <LoginPage />
              </AuthGuard>
            }
          />
          <Route
            path="/register"
            element={
              <AuthGuard>
                <RegisterPage />
              </AuthGuard>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <AuthGuard>
                <ForgotPasswordPage />
              </AuthGuard>
            }
          />

          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route path="/error" element={<ErrorPage />} />
          <Route path="/404" element={<NotFoundPage />} />

          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <AppRoutes
                  layout={layout}
                  isLoading={layoutLoading}
                  isError={layoutError}
                  error={error}
                />
              </ProtectedRoute>
            }
          />

          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to={layout?.routes?.[0] ?? '/app/home'} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRouter;
